import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { EditableContent, EditableContentComponent } from 'components/input/editable-content/editable-content.component';
import { ListRowComponent } from 'components/layout/list-view/list-view.component';
import 'extensions/deep-copy';
import { OrderLineModel } from 'models/generated/Entities.GormCrm.OrderLine';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { Pipes } from 'modules/pipes';
import { ProductService } from 'services/product.service';
import { EditOrderLineComponent } from './edit-order-line.component';
import { ReasonModel } from 'models/generated/Entities.GormCrm.Reason';

@Component({
	selector: 'order-lines',
	templateUrl: 'order-lines.component.html',
	imports: [Layout, Pipes, ListView, EditableContent, EditOrderLineComponent]
})
export class OrderLinesComponent {

	@Output() private orderLineChanged = new EventEmitter();
	private _orderLines: OrderLineModel[] = [];
	@Input()
	public set orderLines(orderLines: OrderLineModel[]) {
		this._orderLines = orderLines;
		this.totalDiscount = orderLines.reduce((sum, orderLine) => sum - (orderLine.discount ?? 0), 0) ?? 0;
	}
	public get orderLines() { return this._orderLines; }
	@Input() public editMode = false;
	@Input() public defaultNumberOfInstallments = 1;
	@Input() public discountReason?: ReasonModel;
	public editorOffsetPosition?: ElementRef<HTMLElement>;
	public totalDiscount = 0;

	@ViewChild('editor') private editor?: EditableContentComponent;
	private editOrderLine?: OrderLineModel;

	constructor(public readonly productService: ProductService) { }

	getDiscountPercent(orderLine: OrderLineModel): number {
		return orderLine.productVariant?.price ? 100 - (orderLine.totalPrice / orderLine.productVariant.price) * 100 : 0;
	}

	editLine(orderLine: OrderLineModel, row?: ListRowComponent) {
		if (!this.editMode || !this.editor) return;
		this.editOrderLine = orderLine;
		this.editorOffsetPosition = row?.getRowElement();
		setTimeout(() => this.editor?.beginEdit(orderLine));
	}

	updateOrderLine(updatedOrderLine: OrderLineModel) {
		if (!this.editOrderLine) return;
		if (this.orderLines.indexOf(this.editOrderLine) < 0) this.orderLines.push(this.editOrderLine);

		// If product relation changes, force creation of a new orderline, so relations to questionnaire answers are intentionally broken
		if (updatedOrderLine.productVariant?.productId != this.editOrderLine.productVariant?.productId) {
			updatedOrderLine.id = undefined;
		}

		// OrderLine must either have "numberOfInstallments" (upfront payment via X installments) or "startupInstallments" (startup amount paid over X installments) or neither. Gorm can handle both, but it doesn't make sense for our sales
		if (updatedOrderLine.numberOfInstallments !== undefined) {
			updatedOrderLine.startupAmountExclFirstInvoice = undefined;
			updatedOrderLine.startupInstallments = undefined;
		}
		if (updatedOrderLine.startupAmountExclFirstInvoice !== undefined) {
			updatedOrderLine.numberOfInstallments = undefined;
			if (updatedOrderLine.startupInstallments === undefined) updatedOrderLine.startupInstallments = this.defaultNumberOfInstallments;
		}

		Object.assign(this.editOrderLine, updatedOrderLine);
		this.orderLineChanged.emit();
	}

	removeEditingOrderLine() {
		if (!this.editOrderLine) return;
		const index = this.orderLines.indexOf(this.editOrderLine);
		if (index >= 0) this.orderLines.splice(index, 1);
		this.editor?.cancelEdit();
		this.orderLineChanged.emit();
	}

	addNewOrderLine() {
		const newOrderLine = new OrderLineModel();
		// By default, change the payment interval if any other orderline has a specific interval set
		newOrderLine.paymentInterval = this.orderLines.find(ol => ol.paymentInterval != 0)?.paymentInterval ?? 0;
		newOrderLine.quantity = 1;
		this.editLine(newOrderLine);
	}

	editingOrderLineCanBeRemoved() {
		return this.orderLines.indexOf(this.editOrderLine!) >= 0;
	}
}
